import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Space, message, Divider, Typography, Alert, Row, Col, Tooltip, Radio, Modal } from 'antd';
import { LockOutlined, CheckCircleOutlined, DollarOutlined, UserOutlined, GlobalOutlined } from '@ant-design/icons';
import { PageHeader } from '../components/PageHeader';
import { AccountClosure } from '../components/AccountClosure'; // S41
import { ClosureStatusBanner } from '../components/ClosureStatusBanner'; // S41
import { MonthlyStatements } from '../components/MonthlyStatements'; // S44
import BankAccountsSection from './BankAccountsSection'; // S-B1
import { useAuth } from '../app/AuthContext';
import { useFeature } from '../contexts/FeatureFlagsContext';
import { useTranslation } from 'react-i18next';
import { PrivacyContentEN } from './PrivacyContentEN';
import { PrivacyContentES } from './PrivacyContentES';
import { TermsContentEN } from './TermsContentEN';
import { TermsContentES } from './TermsContentES';
import api from '../api';

const { Text, Title } = Typography;

// Email Notification Settings Component
interface EmailNotificationSettingsProps {
  userData: UserData | null;
  onUpdate: () => void;
}

function EmailNotificationSettings({ userData, onUpdate }: EmailNotificationSettingsProps) {
  const { t } = useTranslation('settings');
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    email_notifications_enabled: userData?.email_notifications_enabled ?? true,
    notify_profit_allocation: userData?.notify_profit_allocation ?? true,
    notify_weekly_settlement: userData?.notify_weekly_settlement ?? true,
    notify_withdrawal_status: userData?.notify_withdrawal_status ?? true,
    notify_admin_updates: userData?.notify_admin_updates ?? true,
  });

  const hasChanges = userData && (
    preferences.email_notifications_enabled !== userData.email_notifications_enabled ||
    preferences.notify_profit_allocation !== userData.notify_profit_allocation ||
    preferences.notify_weekly_settlement !== userData.notify_weekly_settlement ||
    preferences.notify_withdrawal_status !== userData.notify_withdrawal_status ||
    preferences.notify_admin_updates !== userData.notify_admin_updates
  );

  const handleMasterToggle = (enabled: boolean) => {
    setPreferences({
      email_notifications_enabled: enabled,
      notify_profit_allocation: enabled,
      notify_weekly_settlement: enabled,
      notify_withdrawal_status: enabled,
      notify_admin_updates: enabled,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.users.updateEmailNotifications(preferences);
      message.success(t('preferences.emailNotifications.success'));
      onUpdate();
    } catch (err: any) {
      console.error('Failed to update email notifications:', err);
      message.error(err.message || t('preferences.emailNotifications.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Form.Item style={{ marginBottom: 8 }}>
          <Space>
            <input
              type="checkbox"
              checked={preferences.email_notifications_enabled}
              onChange={(e) => handleMasterToggle(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <Text strong>{t('preferences.emailNotifications.masterToggle')}</Text>
          </Space>
        </Form.Item>
      </div>

      <div style={{ marginLeft: 24 }}>
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Space>
              <input
                type="checkbox"
                checked={preferences.notify_profit_allocation}
                onChange={(e) => setPreferences({ ...preferences, notify_profit_allocation: e.target.checked })}
                disabled={!preferences.email_notifications_enabled}
                style={{ cursor: preferences.email_notifications_enabled ? 'pointer' : 'not-allowed' }}
              />
              <div>
                <Text strong disabled={!preferences.email_notifications_enabled}>
                  {t('preferences.emailNotifications.profitAllocation')}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('preferences.emailNotifications.profitAllocationDesc')}
                </Text>
              </div>
            </Space>
          </div>

          <div>
            <Space>
              <input
                type="checkbox"
                checked={preferences.notify_weekly_settlement}
                onChange={(e) => setPreferences({ ...preferences, notify_weekly_settlement: e.target.checked })}
                disabled={!preferences.email_notifications_enabled}
                style={{ cursor: preferences.email_notifications_enabled ? 'pointer' : 'not-allowed' }}
              />
              <div>
                <Text strong disabled={!preferences.email_notifications_enabled}>
                  {t('preferences.emailNotifications.weeklySettlement')}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('preferences.emailNotifications.weeklySettlementDesc')}
                </Text>
              </div>
            </Space>
          </div>

          <div>
            <Space>
              <input
                type="checkbox"
                checked={preferences.notify_withdrawal_status}
                onChange={(e) => setPreferences({ ...preferences, notify_withdrawal_status: e.target.checked })}
                disabled={!preferences.email_notifications_enabled}
                style={{ cursor: preferences.email_notifications_enabled ? 'pointer' : 'not-allowed' }}
              />
              <div>
                <Text strong disabled={!preferences.email_notifications_enabled}>
                  {t('preferences.emailNotifications.withdrawalStatus')}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('preferences.emailNotifications.withdrawalStatusDesc')}
                </Text>
              </div>
            </Space>
          </div>

          <div>
            <Space>
              <input
                type="checkbox"
                checked={preferences.notify_admin_updates}
                onChange={(e) => setPreferences({ ...preferences, notify_admin_updates: e.target.checked })}
                disabled={!preferences.email_notifications_enabled}
                style={{ cursor: preferences.email_notifications_enabled ? 'pointer' : 'not-allowed' }}
              />
              <div>
                <Text strong disabled={!preferences.email_notifications_enabled}>
                  {t('preferences.emailNotifications.adminUpdates')}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('preferences.emailNotifications.adminUpdatesDesc')}
                </Text>
              </div>
            </Space>
          </div>
        </Space>
      </div>

      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
        >
          {t('preferences.emailNotifications.saveButton')}
        </Button>
      </div>
    </Space>
  );
}

interface UserData {
  full_name: string;
  email: string;
  country: string;
  account_id?: number;
  locale?: string;
  company_cut_percent?: number;
  tenure_description?: string;
  participation_status?: 'active' | 'pending_close' | 'exited'; // S41
  email_notifications_enabled?: boolean;
  notify_profit_allocation?: boolean;
  notify_weekly_settlement?: boolean;
  notify_withdrawal_status?: boolean;
  notify_admin_updates?: boolean;
}

export function Settings() {
  const { t } = useTranslation('settings');
  const isPlaidEnabled = useFeature('plaid');
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [selectedLocale, setSelectedLocale] = useState('en');
  const [savingLocale, setSavingLocale] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [hasLinkedAccount, setHasLinkedAccount] = useState(false);
  const { updateUserLocale } = useAuth();

  useEffect(() => {
    fetchUserData();
    fetchPaymentProfile();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoadingProfile(true);
      const response = await api.users.me();
      setUserData(response.user);
      setSelectedLocale(response.user.locale || 'en');
    } catch (err: any) {
      console.error('Failed to fetch user data:', err);
      message.error(err.message || t('messages.loadError'));
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchPaymentProfile = async () => {
    try {
      const response = await api.payments.getProfile();
      setHasLinkedAccount(response.has_linked_account);
    } catch (err: any) {
      console.error('Failed to fetch payment profile:', err);
      // Silently fail - not critical for Settings page load
    }
  };

  const handleSaveLocale = async () => {
    try {
      setSavingLocale(true);
      await api.users.updateLocale(selectedLocale);
      updateUserLocale(selectedLocale);
      await fetchUserData();
      message.success(t('preferences.language.success'));
    } catch (err: any) {
      console.error('Failed to update locale:', err);
      message.error(err.message || t('preferences.language.error'));
    } finally {
      setSavingLocale(false);
    }
  };

  // S41: Handle account closure cancellation
  const handleCancelClosure = async () => {
    try {
      await api.users.cancelAccountClosure();
      message.success(t('accountManagement.closureCancelSuccess', 'Account closure request cancelled'));
      await fetchUserData();
    } catch (err: any) {
      console.error('Failed to cancel closure:', err);
      message.error(err.message || t('accountManagement.closureCancelError', 'Failed to cancel closure request'));
    }
  };

  const handleChangePassword = async (values: {
    current_password: string;
    new_password: string;
    new_password_confirmation: string;
  }) => {
    try {
      setLoading(true);
      await api.users.changePassword(
        values.current_password,
        values.new_password,
        values.new_password_confirmation
      );
      message.success(t('security.changePassword.success'));
      form.resetFields();
    } catch (err: any) {
      console.error('Failed to change password:', err);
      message.error(err.message || t('security.changePassword.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = () => {
    editForm.setFieldsValue({
      full_name: userData?.full_name,
      email: userData?.email,
    });
    setEditModalOpen(true);
  };

  const handleUpdateProfile = async () => {
    try {
      const values = await editForm.validateFields();
      setSavingProfile(true);
      await api.users.updateProfile(values.full_name, values.email);
      await fetchUserData();
      message.success(t('accountInfo.editModal.success'));
      setEditModalOpen(false);
    } catch (err: any) {
      if (err.errorFields) {
        // Form validation error - do nothing, Ant Design will show the error
        return;
      }
      console.error('Failed to update profile:', err);
      message.error(err.message || t('accountInfo.editModal.error'));
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
      />

      {/* Basic Information - Editable */}
      <Card
        title={t('accountInfo.title')}
        variant="borderless"
        style={{ marginBottom: 24 }}
        loading={loadingProfile}
        extra={
          <Button type="primary" onClick={handleOpenEditModal}>
            {t('accountInfo.editButton')}
          </Button>
        }
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Space>
                <UserOutlined style={{ fontSize: 16, color: '#1677ff' }} />
                <Text strong>{t('accountInfo.accountId')}:</Text>
                <Text>{userData?.account_id}</Text>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space>
                <UserOutlined style={{ fontSize: 16, color: '#1677ff' }} />
                <Text strong>{t('accountInfo.fullName')}:</Text>
                <Text>{userData?.full_name}</Text>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space>
                <UserOutlined style={{ fontSize: 16, color: '#1677ff' }} />
                <Text strong>{t('accountInfo.email')}:</Text>
                <Text>{userData?.email}</Text>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space>
                <GlobalOutlined style={{ fontSize: 16, color: userData?.country === 'USA' ? '#1677ff' : '#9254de' }} />
                <Text strong>{t('accountInfo.country')}:</Text>
                <Text>{userData?.country === 'USA' ? t('accountInfo.unitedStates') : userData?.country}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ({t('accountInfo.immutable')})
                </Text>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space>
                <DollarOutlined style={{ fontSize: 16, color: '#52c41a' }} />
                <Text strong>{t('accountInfo.companyCut')}:</Text>
                <Text>{userData?.company_cut_percent?.toFixed(0)}%</Text>
                <Tooltip title={t('accountInfo.tenure')}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ({userData?.tenure_description})
                  </Text>
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* S-B1: Bank Accounts */}
      {isPlaidEnabled && (
        <BankAccountsSection 
          hasLinkedAccount={hasLinkedAccount} 
          onAccountLinked={fetchPaymentProfile}
        />
      )}

      {/* Security Settings */}
      <Card
        title={
          <Space>
            <LockOutlined />
            <span>{t('security.title')}</span>
          </Space>
        }
        variant="borderless"
        style={{ marginBottom: 24 }}
      >
        <Title level={5} style={{ marginBottom: 16 }}>{t('security.changePassword.title')}</Title>
        
        <Alert
          title={t('security.changePassword.requirements.title')}
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>{t('security.changePassword.requirements.minLength')}</li>
              <li>{t('security.changePassword.requirements.complexity')}</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleChangePassword}
          style={{ maxWidth: '100%' }}
        >
          <Form.Item
            label={t('security.changePassword.currentPassword.label')}
            name="current_password"
            rules={[
              { required: true, message: t('security.changePassword.currentPassword.required') }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('security.changePassword.currentPassword.placeholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label={t('security.changePassword.newPassword.label')}
            name="new_password"
            rules={[
              { required: true, message: t('security.changePassword.newPassword.required') },
              { min: 8, message: t('security.changePassword.newPassword.minLength') }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('security.changePassword.newPassword.placeholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label={t('security.changePassword.confirmPassword.label')}
            name="new_password_confirmation"
            dependencies={['new_password']}
            rules={[
              { required: true, message: t('security.changePassword.confirmPassword.required') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('security.changePassword.confirmPassword.mismatch')));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<CheckCircleOutlined />}
              placeholder={t('security.changePassword.confirmPassword.placeholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              icon={<LockOutlined />}
            >
              {t('security.changePassword.button')}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Future Settings Sections */}
      <Card
        title={t('preferences.title')}
        variant="borderless"
        style={{ marginBottom: 24 }}
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>{t('preferences.language.title')}</Text>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <Radio.Group 
                value={selectedLocale} 
                onChange={(e) => setSelectedLocale(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="en">{t('preferences.language.english')}</Radio.Button>
                <Radio.Button value="es">{t('preferences.language.spanish')}</Radio.Button>
              </Radio.Group>
              <Button
                type="primary"
                onClick={handleSaveLocale}
                loading={savingLocale}
                disabled={selectedLocale === userData?.locale}
              >
                {t('preferences.language.saveButton')}
              </Button>
            </div>
            <Divider style={{ margin: '16px 0' }} />
          </div>

          <div>
            <Text strong>{t('preferences.emailNotifications.title')}</Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {t('preferences.emailNotifications.description')}
            </Text>
            {loadingProfile ? null : (
              <EmailNotificationSettings userData={userData} onUpdate={fetchUserData} />
            )}
          </div>
        </Space>
      </Card>

      {/* Account Management */}
      <Card
        title={t('accountManagement.title')}
        variant="borderless"
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {/* S41: Account Closure Status Banner */}
          {userData && userData.participation_status && userData.participation_status !== 'active' && (
            <div style={{ marginBottom: 16 }}>
              <ClosureStatusBanner 
                participationStatus={userData.participation_status} 
                onCancelClosure={userData.participation_status === 'pending_close' ? handleCancelClosure : undefined}
                canCancelClosure={userData.participation_status === 'pending_close'}
              />
            </div>
          )}

          {/* S41: Account Closure Section */}
          {userData && userData.participation_status === 'active' && (
            <div style={{ marginBottom: 16 }}>
              <AccountClosure onStatusChange={fetchUserData} />
              <Divider style={{ margin: '16px 0' }} />
            </div>
          )}

          {/* S44: Monthly Statements */}
          <div style={{ marginBottom: 16 }}>
            <MonthlyStatements />
            <Divider style={{ margin: '16px 0' }} />
          </div>

          <div>
            <Text strong>{t('accountManagement.exportData.title')}</Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              {t('accountManagement.exportData.comingSoon')}
            </Text>
          </div>
        </Space>
      </Card>

      {/* Privacy Policy and Terms Links */}
      <div style={{ textAlign: 'center', marginTop: 24, marginBottom: 24 }}>
        <a onClick={() => setPrivacyModalOpen(true)} style={{ cursor: 'pointer' }}>
          {t('privacy.link')}
        </a>
        {' | '}
        <a onClick={() => setTermsModalOpen(true)} style={{ cursor: 'pointer' }}>
          {t('terms.link')}
        </a>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        title={t('accountInfo.editModal.title')}
        open={editModalOpen}
        onOk={handleUpdateProfile}
        onCancel={() => setEditModalOpen(false)}
        confirmLoading={savingProfile}
        okText={t('accountInfo.editModal.saveButton')}
        cancelText={t('accountInfo.editModal.cancelButton')}
      >
        <Form
          form={editForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="full_name"
            label={t('accountInfo.editModal.fullNameLabel')}
            rules={[
              { required: true, message: t('accountInfo.editModal.fullNameRequired') }
            ]}
          >
            <Input 
              prefix={<UserOutlined />}
              placeholder={t('accountInfo.editModal.fullNamePlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={t('accountInfo.editModal.emailLabel')}
            rules={[
              { required: true, message: t('accountInfo.editModal.emailRequired') },
              { type: 'email', message: t('accountInfo.editModal.emailInvalid') }
            ]}
          >
            <Input 
              prefix={<UserOutlined />}
              placeholder={t('accountInfo.editModal.emailPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal
        title={selectedLocale === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
        open={privacyModalOpen}
        onCancel={() => setPrivacyModalOpen(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px 0' }}>
          {selectedLocale === 'en' ? <PrivacyContentEN /> : <PrivacyContentES />}
        </div>
      </Modal>

      {/* Terms and Conditions Modal */}
      <Modal
        title={selectedLocale === 'en' ? 'Terms and Conditions' : 'Términos y Condiciones'}
        open={termsModalOpen}
        onCancel={() => setTermsModalOpen(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px 0' }}>
          {selectedLocale === 'en' ? <TermsContentEN /> : <TermsContentES />}
        </div>
      </Modal>
    </div>
  );
}

export default Settings;
